/**
 * TypeScript types for the per-agent control surface (SPEC §2.6).
 *
 * One uniform settings pattern for all 10 agents. Platform floors arrive in
 * `platform_policy` and are DISPLAY-ONLY: the UI renders them as fixed
 * policy, never as toggles — the server rejects any attempt to cross them.
 */

export type AgentDomain =
  | 'product_marketing'
  | 'demand_gen'
  | 'paid_media'
  | 'content'
  | 'seo'
  | 'lifecycle'
  | 'abm'
  | 'sdr'
  | 'revops'
  | 'orchestrator';

export type AutonomyLevel = 'manual' | 'auto_tier1';

export interface BudgetCaps {
  daily_usd?: number | null;
  monthly_usd?: number | null;
}

export interface SafetyCaps {
  max_sends_per_contact_per_week?: number;
  min_deliverability_score?: number;
  min_list_health_score?: number;
  [key: string]: number | undefined;
}

/**
 * Who is reading this response (spec #47 D5). An operator acts as
 * THEMSELVES — their own user id, never an impersonated tenant user.
 */
export interface Actor {
  role: 'tenant' | 'operator';
  user_id: string | null;
}

/**
 * Whether one field is editable by THIS actor, and why not (spec #47 D5).
 *
 * Rendering, never enforcement: the server refuses a crossing write with a
 * 422 whatever this said. `locked_by: 'operator'` alongside `editable:
 * true` means "reachable, but only past the tenant's own plan" — the
 * operator ceiling, not a lock on the reader.
 */
export interface FieldPolicyEntry {
  editable: boolean;
  locked_by: 'plan' | 'operator' | 'platform' | null;
  required_plan?: string | null;
  reason?: string | null;
  floor?: Record<string, unknown> | null;
}

export type FieldPolicy = Record<string, FieldPolicyEntry>;

export interface AgentSettings {
  domain: AgentDomain;
  display_name: string;
  configured: boolean;
  source: 'tenant' | 'system' | null;
  enabled: boolean;
  autonomy_level: AutonomyLevel;
  exploration_fraction: number;
  budget_caps: BudgetCaps;
  safety_caps: SafetyCaps;
  allowed_action_types: string[];
  allowed_platforms: string[];
  system_allowed_action_types: string[];
  system_allowed_platforms: string[];
  /** System row always — there is no tenant value (#47 §3, P7). */
  system_allowed_proxy_metrics?: string[];
  owned_questions: string[];
  version: number | null;
  /** spec #47 D5 — optional so a pre-D5 fixture still typechecks. */
  actor?: Actor | null;
  field_policy?: FieldPolicy;
}

export interface SafetyCapFloor {
  platform_limit: number;
  direction: 'max' | 'min';
  label: string;
}

export interface PlatformPolicy {
  tier_floors: {
    spend_always_tier2: boolean;
    tier2_floor_action_types: string[];
    tier1_eligible_action_types: string[];
    description: string;
  };
  exploration_bounds: {
    min: number;
    max: number;
    description: string;
  };
  safety_cap_floors: Record<string, SafetyCapFloor>;
  autonomy_levels: AutonomyLevel[];
}

export interface AgentsListResponse {
  agents: AgentSettings[];
  platform_policy: PlatformPolicy;
}

export interface AgentSettingsUpdate {
  enabled?: boolean;
  autonomy_level?: AutonomyLevel;
  exploration_fraction?: number;
  budget_caps?: BudgetCaps;
  safety_caps?: SafetyCaps;
  allowed_action_types?: string[];
  allowed_platforms?: string[];
}

export function getAutonomyLevelLabel(level: AutonomyLevel): string {
  const labels: Record<AutonomyLevel, string> = {
    manual: 'Fully manual — propose everything, nothing auto',
    auto_tier1: 'Auto Tier-1 — cheap reversible tests run themselves',
  };
  return labels[level] || level;
}

/** Agents in the platform's dependency order (SPEC §7: PMM first). */
export const AGENT_DEPENDENCY_ORDER: AgentDomain[] = [
  'orchestrator',
  'product_marketing',
  'revops',
  'demand_gen',
  'paid_media',
  'content',
  'seo',
  'lifecycle',
  'abm',
  'sdr',
];

// ---------------------------------------------------------------------------
// Enablement switchboard (ENGINEERING-PLAN §7): agents come online in
// dependency order; each carries its enablement checklist.
// ---------------------------------------------------------------------------

export interface EnablementPrerequisite {
  domain: string;
  enabled: boolean;
}

export interface EnablementChecklist {
  platform_rollout: boolean;
  /** Doc path when authored; null = doc missing, agent cannot be enabled. */
  stress_test_doc: string | null;
  prerequisites: EnablementPrerequisite[];
}

export interface EnablementEntry {
  domain: AgentDomain;
  display_name: string;
  order: number;
  configured: boolean;
  /** True whenever a tenant row (or ambient default) marks this agent on —
   * NOT the same as actually reasoning. An agent can be `enabled` while
   * blocked (e.g. its own stress-test doc is missing) and the reasoning
   * worker silently skips it every cycle; render on `runnable`, not this. */
  enabled: boolean;
  checklist: EnablementChecklist;
  can_enable: boolean;
  blockers: string[];
  /** True only when this agent is BOTH `enabled` and has no enablement
   * blockers — the worker actually reasons on it. `enabled && !runnable`
   * is the silent-skip trap: badge that state distinctly and show the
   * blockers text even though `enabled` is true. */
  runnable: boolean;
}

export interface EnablementBoard {
  agents: EnablementEntry[];
  /** Human-gated switch: reasoning runs, every proposal waits in the inbox. */
  agents_enabled: boolean;
  /** Additionally auto-approve Tier-1-eligible proposals by policy. */
  tier1_auto_enabled: boolean;
}
